const firstValue = (...values) => {
  for (const value of values) {
    if (value != null && value !== '') return value;
  }
  return '';
};

const numberValue = (...values) => {
  const value = firstValue(...values);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getPartnerCode = (partner) => firstValue(
  partner?.Code,
  partner?.code,
  partner?.partnerCode,
  partner?.PartnerCode
);

const getPartnerId = (partner) => firstValue(
  partner?.id,
  partner?.Id,
  partner?.partnerId,
  partner?.PartnerId,
  partner?.ticketTrackingPartnerId,
  partner?.TicketTrackingPartnerId,
  partner?.ticketTrackingPartnerID
);

// ── DisplayConfig ─────────────────────────────────────────────────────────────
const DEFAULT_DISPLAY_CONFIG = {
  surfaces: ['PurchasePage', 'Checkout'],
  requiredOn: [],
  displayAs: 'Dropdown',
};

/**
 * Parses the JSON DisplayConfig string from the API into a plain object.
 * Falls back to the default config (PurchasePage + Checkout, Dropdown, not
 * required) for partners created before the column existed (null / empty).
 */
export const parseDisplayConfig = (raw) => {
  if (!raw) return { ...DEFAULT_DISPLAY_CONFIG };
  if (typeof raw === 'object') return raw; // already parsed upstream
  try {
    return JSON.parse(raw);
  } catch {
    return { ...DEFAULT_DISPLAY_CONFIG };
  }
};

/** Returns true when the partner should appear on the given surface. */
export const partnerIsOnSurface = (partner, surface) => {
  const cfg = parseDisplayConfig(partner?.displayConfig ?? partner?.DisplayConfig);
  return cfg.surfaces.some(s => s.toLowerCase() === surface.toLowerCase());
};

/** Returns true when selecting this partner is mandatory on the given surface. */
export const partnerIsRequiredOn = (partner, surface) => {
  const cfg = parseDisplayConfig(partner?.displayConfig ?? partner?.DisplayConfig);
  return cfg.requiredOn.some(s => s.toLowerCase() === surface.toLowerCase());
};

/** Returns true when the partner has no surfaces (silent / revenue-split). */
export const partnerIsSilent = (partner) => {
  const cfg = parseDisplayConfig(partner?.displayConfig ?? partner?.DisplayConfig);
  return cfg.surfaces.length === 0;
};

/**
 * Returns true when a partner selection is required on the given surface.
 * Checks the field-level DisplayConfig first, then any per-partner config.
 */
export const isRequiredOnSurface = (partners, surface, fieldDisplayConfig = null) => {
  if (!surface) return false;
  const normalizedSurface = String(surface).toLowerCase();
  if (fieldDisplayConfig?.requiredOn?.some(s => String(s).toLowerCase() === normalizedSurface)) {
    return true;
  }
  return (partners || []).some((partner) => partnerIsRequiredOn(partner, surface));
};

// A partner is "active" when it has not been disabled. Disabled partners are
// never shown anywhere. Visibility (isVisible) is a separate concern: it only
// governs whether a partner appears in the public checkout/hero dropdown.
const isActivePartner = (partner) => (
  partner?.isDisabled !== true &&
  partner?.IsDisabled !== true
);

const isVisiblePartner = (partner) => (
  partner?.isVisible !== false &&
  partner?.IsVisible !== false &&
  isActivePartner(partner)
);

export const normalizePartner = (partner, fallback = {}) => {
  const id = firstValue(getPartnerId(partner), fallback.id);
  if (!id) return null;

  // Per-partner DisplayConfig takes precedence; fall back to the field-level
  // default passed in via fallback.displayConfig (e.g. from the API payload).
  const rawDisplayConfig = partner?.displayConfig
    ?? partner?.DisplayConfig
    ?? fallback.displayConfig
    ?? null;

  const orgName = firstValue(
    partner?.organization_name,
    partner?.organizationName,
    partner?.OrganizationName,
    partner?.partner_name,
    partner?.partnerName,
    partner?.PartnerName,
    partner?.name,
    partner?.Name,
    fallback.name
  );

  const orgLogoUrl = firstValue(
    partner?.organization_logo_url,
    partner?.organizationLogoUrl,
    partner?.OrganizationLogoUrl,
    partner?.partner_logo_url,
    partner?.partnerLogoUrl,
    partner?.PartnerLogoUrl,
    partner?.logo_url,
    partner?.logoUrl,
    partner?.LogoUrl,
    null
  ) || null;

  return {
    id,
    Code: getPartnerCode(partner),
    organization_name: orgName,
    name: orgName,
    organization_logo_url: orgLogoUrl,
    logoUrl: orgLogoUrl,
    supporter_count: numberValue(
      partner?.supporter_count,
      partner?.supporterCount,
      partner?.SupporterCount
    ),
    total_tickets: numberValue(
      partner?.total_tickets,
      partner?.totalTickets,
      partner?.TotalTickets
    ),
    total_amount: numberValue(
      partner?.total_amount,
      partner?.totalAmount,
      partner?.TotalAmount
    ),
    sortOrder: numberValue(
      partner?.sortOrder,
      partner?.SortOrder
    ),
    // Preserve the raw DisplayConfig string so surface-checks can be done at
    // call sites without re-fetching. parseDisplayConfig handles null / empty.
    displayConfig: rawDisplayConfig,
    pageConfig: firstValue(partner?.pageConfig, partner?.page_config, partner?.PageConfig, null) || null,
  };
};

export const sortPartnersByRank = (partners) => [...partners].sort((a, b) => {
  if (b.total_amount !== a.total_amount) return b.total_amount - a.total_amount;
  if (b.total_tickets !== a.total_tickets) return b.total_tickets - a.total_tickets;
  if (b.supporter_count !== a.supporter_count) return b.supporter_count - a.supporter_count;
  return a.organization_name.localeCompare(b.organization_name);
});

export const sortPartnersByName = (partners) => [...partners].sort((a, b) =>
  a.organization_name.localeCompare(b.organization_name)
);

export const sortPartnersByOrder = (partners) => [...partners].sort((a, b) => {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.organization_name.localeCompare(b.organization_name);
});

export function mergePartnerData(fieldPartners = [], statsPartners = [], limit = null, { includeHidden = false, surface = null, displayConfig: fieldDisplayConfig = null, partnerSortOrder = 'alphabetical' } = {}) {
  const statsById = new Map();

  for (const partner of statsPartners) {
    // Public display surfaces (leaderboard, all-partners, top-partners) pass
    // includeHidden so partners hidden from the checkout dropdown still appear
    // with their fundraising stats. The dropdown keeps the visible-only default.
    const allowed = includeHidden ? isActivePartner(partner) : isVisiblePartner(partner);
    if (!allowed) continue;

    // When a surface is specified, only include partners whose DisplayConfig
    // lists that surface. Partners with no explicit DisplayConfig (pre-migration)
    // are treated as visible on all surfaces for backward compatibility.
    // Use the field-level default as a fallback for the surface check.
    const rawDisplayConfig = partner?.displayConfig
      ?? partner?.DisplayConfig
      ?? fieldDisplayConfig
      ?? null;
    if (surface && rawDisplayConfig && !partnerIsOnSurface({ displayConfig: rawDisplayConfig }, surface)) continue;

    const normalized = normalizePartner(partner, { displayConfig: fieldDisplayConfig });
    if (normalized) statsById.set(String(normalized.id), normalized);
  }

  let merged;
  if (fieldPartners.length > 0) {
    merged = fieldPartners
      .map((fieldPartner) => {
        const stats = statsById.get(String(fieldPartner.id));
        return stats || normalizePartner({ id: fieldPartner.id, name: fieldPartner.name }, { displayConfig: fieldDisplayConfig });
      })
      .filter(Boolean);
  } else {
    merged = [...statsById.values()];
  }

  let sorted;
  if (partnerSortOrder === 'alphabetical') {
    sorted = sortPartnersByName(merged);
  } else if (partnerSortOrder === 'asEntered') {
    sorted = sortPartnersByOrder(merged);
  } else {
    sorted = sortPartnersByRank(merged);
  }
  return limit != null && limit > 0 ? sorted.slice(0, limit) : sorted;
}

/**
 * Resolves a localized string from a pageConfig dictionary field.
 * Handles language dictionaries in any format:
 * - JSON strings e.g. '{"en":"...","fr":"..."}'
 * - Objects with ISO codes: { en: "...", fr: "..." } or { EN: "...", FR: "..." }
 * - Objects with English/French/Spanish names: { English: "...", French: "..." }
 * - Objects with regional codes: { fr_CA: "...", en_CA: "..." }
 * Falls back safely through active lang -> English -> first non-empty value.
 */
export function getLocalizedPartnerContent(fieldDict, currentLang = 'en', defaultLang = 'English') {
  if (!fieldDict) return '';

  let dictObj = fieldDict;
  if (typeof fieldDict === 'string') {
    const trimmed = fieldDict.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        dictObj = JSON.parse(trimmed);
      } catch {
        return fieldDict;
      }
    } else {
      return fieldDict;
    }
  }

  if (typeof dictObj !== 'object' || dictObj === null) {
    return String(dictObj || '');
  }

  const normalizedLang = String(currentLang || 'en').toLowerCase().trim();

  // Alias lists for supported languages
  const langAliases = {
    fr: ['fr', 'FR', 'French', 'french', 'fr_CA', 'fr-CA', 'fr_FR', 'fr-FR'],
    en: ['en', 'EN', 'English', 'english', 'en_CA', 'en-CA', 'en_US', 'en-US'],
    es: ['es', 'ES', 'Spanish', 'spanish', 'es_ES', 'es-ES'],
  };

  const currentCandidates = langAliases[normalizedLang] || [normalizedLang, currentLang];

  for (const alias of currentCandidates) {
    if (dictObj[alias] && String(dictObj[alias]).trim()) {
      return String(dictObj[alias]);
    }
  }

  // Fallback to English aliases
  for (const alias of langAliases.en) {
    if (dictObj[alias] && String(dictObj[alias]).trim()) {
      return String(dictObj[alias]);
    }
  }

  // Fallback to custom defaultLang (e.g. 'English')
  if (defaultLang && dictObj[defaultLang] && String(dictObj[defaultLang]).trim()) {
    return String(dictObj[defaultLang]);
  }

  // Return first available non-empty string value in object
  const values = Object.values(dictObj).filter(v => v != null && String(v).trim());
  return values.length > 0 ? String(values[0]) : '';
}